// Card read-only de dieta + edicion inline.
// codigo y nombre son inmutables (PUT /nutricionista/dietas/{id}).
// S8 T26: calorias, CHO, Prot y Grasa ya son editables.
import { useState } from 'react'
import type { DietaResponse, DietaUpdateBody } from '../../services/nutricionista'

interface DietaCardProps {
  dieta: DietaResponse
  onSave: (body: DietaUpdateBody) => Promise<void> | void
}

function numToInput(n: number | null): string {
  return n === null || n === undefined ? '' : String(n)
}

function parseNumOrNull(v: string): number | null {
  if (v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export default function DietaCard({ dieta, onSave }: DietaCardProps) {
  const [editing, setEditing] = useState(false)
  const [calorias, setCalorias] = useState(numToInput(dieta.calorias))
  const [choPct, setChoPct] = useState(numToInput(dieta.cho_pct))
  const [protPct, setProtPct] = useState(numToInput(dieta.prot_pct))
  const [grasaPct, setGrasaPct] = useState(numToInput(dieta.grasa_pct))
  const [obs, setObs] = useState(dieta.observaciones ?? '')
  const [activo, setActivo] = useState(dieta.activo)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function resetEdits() {
    setCalorias(numToInput(dieta.calorias))
    setChoPct(numToInput(dieta.cho_pct))
    setProtPct(numToInput(dieta.prot_pct))
    setGrasaPct(numToInput(dieta.grasa_pct))
    setObs(dieta.observaciones ?? '')
    setActivo(dieta.activo)
    setError(null)
  }

  async function handleSave() {
    setError(null)
    const cho = parseNumOrNull(choPct)
    const prot = parseNumOrNull(protPct)
    const grasa = parseNumOrNull(grasaPct)
    // Validacion suave: si los 3 macros estan presentes, sumar ~100% (tolerancia 5%)
    if (cho !== null && prot !== null && grasa !== null) {
      const suma = cho + prot + grasa
      if (Math.abs(suma - 100) > 5) {
        setError(`CHO + Prot + Grasa = ${suma.toFixed(1)}%. Deberia sumar ~100% (tolerancia 5%).`)
        return
      }
    }
    setSubmitting(true)
    try {
      await onSave({
        calorias: parseNumOrNull(calorias),
        cho_pct: cho,
        prot_pct: prot,
        grasa_pct: grasa,
        observaciones: obs.trim() || null,
        activo,
      })
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <article
      className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-avante-accent"
      aria-labelledby={`dieta-${dieta.id}-title`}
    >
      <header className="flex justify-between items-start mb-2">
        <div>
          <h3 id={`dieta-${dieta.id}-title`} className="font-bold text-base">
            {dieta.codigo} — {dieta.nombre}
          </h3>
          <p className="text-xs text-gray-500">
            {dieta.activo ? 'Activa' : 'Inactiva'}
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-sm text-avante-primary underline"
            aria-label={`Editar dieta ${dieta.codigo}`}
          >
            Editar
          </button>
        )}
      </header>

      {!editing ? (
        <>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-700 dark:text-gray-300">
            <div><dt className="inline font-semibold">Calorias:</dt> <dd className="inline">{dieta.calorias ?? '—'}</dd></div>
            <div><dt className="inline font-semibold">CHO%:</dt> <dd className="inline">{dieta.cho_pct ?? '—'}</dd></div>
            <div><dt className="inline font-semibold">Prot%:</dt> <dd className="inline">{dieta.prot_pct ?? '—'}</dd></div>
            <div><dt className="inline font-semibold">Grasa%:</dt> <dd className="inline">{dieta.grasa_pct ?? '—'}</dd></div>
          </dl>
          {dieta.observaciones && (
            <p className="text-sm mt-2 whitespace-pre-wrap">
              <span className="font-semibold">Observaciones:</span> {dieta.observaciones}
            </p>
          )}
        </>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-semibold">Calorias (kcal)</span>
              <input
                type="number"
                min="0"
                max="5000"
                value={calorias}
                onChange={(e) => setCalorias(e.target.value)}
                disabled={submitting}
                aria-label="Calorias"
                className="mt-1 w-full border rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold">CHO %</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={choPct}
                onChange={(e) => setChoPct(e.target.value)}
                disabled={submitting}
                aria-label="CHO porcentaje"
                className="mt-1 w-full border rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold">Prot %</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={protPct}
                onChange={(e) => setProtPct(e.target.value)}
                disabled={submitting}
                aria-label="Proteina porcentaje"
                className="mt-1 w-full border rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold">Grasa %</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={grasaPct}
                onChange={(e) => setGrasaPct(e.target.value)}
                disabled={submitting}
                aria-label="Grasa porcentaje"
                className="mt-1 w-full border rounded px-2 py-1 text-sm"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold">Observaciones</span>
            <textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              rows={3}
              aria-label="Observaciones"
              className="mt-1 w-full border rounded px-2 py-1 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              aria-label="Activa"
            />
            Activa
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { resetEdits(); setEditing(false) }}
              disabled={submitting}
              className="flex-1 border py-1 rounded text-sm disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting}
              className="flex-1 bg-avante-primary text-white py-1 rounded text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </article>
  )
}
